import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  cn,
  toast,
} from '@startupquickstart/frontend';

import { RichTextDisplay } from '@/components/shared/RichTextDisplay';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ImageOff,
  Sparkles,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import {
  SUBMISSION_STATUS_META,
  formatRelativeTime,
} from '@/components/submissions/submissionStatus';
import { challengeMediaType, isVideoProofUrl } from '@/lib/media';
import {
  PHOTO_STATUS,
  REJECTION_REASONS,
  REVIEW_ACTION,
  useReviewSubmission,
  type ReviewAction,
  type Submission,
} from '@/components/submissions/useSubmissions';

/** Micro-label chrome reused across the submissions surface. */
const MICRO_LABEL = 'font-mono text-[0.6875rem] tracking-[0.15em] uppercase text-muted-foreground';

interface SubmissionReviewDrawerProps {
  eventId: string;
  /** The current filtered list the drawer navigates through. */
  submissions: Submission[];
  /** Index of the submission to open on mount / when reopened. */
  initialIndex: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** True when a keyboard event originates from an editable field. */
function isFromEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

/**
 * Lightbox-style review queue over the current filtered submissions. Shows a
 * large photo (or answer text), full metadata, the AI verdict, a points input
 * defaulting to the challenge's points, and Approve/Reject actions.
 *
 * Keyboard: ArrowLeft/ArrowRight navigate, `A` approves, `R` rejects. After a
 * review the queue auto-advances to the next pending submission (or closes
 * when none remain).
 */
export function SubmissionReviewDrawer({
  eventId,
  submissions,
  initialIndex,
  open,
  onOpenChange,
}: SubmissionReviewDrawerProps) {
  const [index, setIndex] = useState(initialIndex);
  const [points, setPoints] = useState<number>(0);
  // Reject is a two-step verdict: choosing "Reject" opens the reason picker
  // (saved codes or free text) and only "Confirm reject" submits.
  const [rejecting, setRejecting] = useState(false);
  const [reasonChoice, setReasonChoice] = useState<string | null>(null);
  const [customReason, setCustomReason] = useState('');
  const review = useReviewSubmission(eventId);

  // Reset to the requested submission whenever the drawer (re)opens.
  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  const current: Submission | undefined = submissions[index];

  // Default the points field to the challenge's points each time the active
  // submission changes, and drop any half-finished rejection.
  useEffect(() => {
    if (current) setPoints(current.challenge.points);
    setRejecting(false);
    setReasonChoice(null);
    setCustomReason('');
  }, [current]);

  const goTo = useCallback(
    (next: number) => {
      if (next < 0 || next > submissions.length - 1) return;
      setIndex(next);
    },
    [submissions.length],
  );

  /** Advance to the next pending submission after the current index. */
  const advanceToNextPending = useCallback(() => {
    const next = submissions.findIndex(
      (s, i) => i > index && s.photoStatus === PHOTO_STATUS.pending,
    );
    if (next !== -1) setIndex(next);
    else onOpenChange(false);
  }, [submissions, index, onOpenChange]);

  // Free text wins over a picked chip so "almost right" edits stick.
  const rejectionReason = customReason.trim() || reasonChoice || '';

  const submitReview = useCallback(
    (action: ReviewAction) => {
      if (!current || current.photoStatus !== PHOTO_STATUS.pending || review.isPending) return;
      if (action === REVIEW_ACTION.reject && !rejectionReason) return;
      review.mutate(
        {
          submissionId: current.id,
          action,
          ...(action === REVIEW_ACTION.approve ? { points } : {}),
          ...(action === REVIEW_ACTION.reject ? { reason: rejectionReason } : {}),
        },
        {
          onSuccess: () => {
            toast.success(action === REVIEW_ACTION.approve ? 'Submission approved' : 'Submission rejected');
            advanceToNextPending();
          },
          onError: () => toast.error('Could not save review. It may already be reviewed.'),
        },
      );
    },
    [current, review, points, rejectionReason, advanceToNextPending],
  );

  // Keyboard shortcuts while the drawer is open.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (isFromEditableTarget(e.target)) return;
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          goTo(index + 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          goTo(index - 1);
          break;
        case 'a':
        case 'A':
          e.preventDefault();
          submitReview(REVIEW_ACTION.approve);
          break;
        case 'r':
        case 'R':
          e.preventDefault();
          setRejecting(true);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, index, goTo, submitReview]);

  if (!current) return null;

  const status = current.photoStatus ?? PHOTO_STATUS.pending;
  const meta = SUBMISSION_STATUS_META[status];
  const StatusIcon = meta.icon;
  const isPending = status === PHOTO_STATUS.pending;
  const hasPhoto = Boolean(current.photoProofUrl);
  const isVideo =
    hasPhoto && isVideoProofUrl(current.photoProofUrl, challengeMediaType(current.challenge));
  const confidencePct =
    current.aiConfidence != null
      ? Math.round(Math.min(Math.max(current.aiConfidence, 0), 1) * 100)
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <DialogHeader className="flex flex-row items-center justify-between border-b px-6 py-4">
          <div className="min-w-0">
            <DialogTitle className="truncate text-base">{current.team.name}</DialogTitle>
            <DialogDescription className="truncate">{current.challenge.name}</DialogDescription>
          </div>
          <Badge variant={meta.badgeVariant} className="gap-1">
            <StatusIcon className="h-3 w-3" aria-hidden="true" />
            {meta.label}
          </Badge>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="relative flex aspect-video w-full items-center justify-center overflow-hidden bg-muted">
            {hasPhoto && isVideo ? (
              <video
                key={current.id}
                src={current.photoProofUrl ?? undefined}
                controls
                playsInline
                preload="metadata"
                className="h-full w-full object-contain"
                aria-label={`${current.team.name} — ${current.challenge.name}`}
              />
            ) : hasPhoto ? (
              <img
                src={current.photoProofUrl ?? undefined}
                alt={`${current.team.name} — ${current.challenge.name}`}
                className="h-full w-full object-contain"
              />
            ) : current.answer ? (
              <p className="max-w-md px-8 text-center text-lg font-medium text-foreground">
                &ldquo;{current.answer}&rdquo;
              </p>
            ) : (
              <ImageOff className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
            )}

            <button
              type="button"
              aria-label="Previous submission"
              onClick={() => goTo(index - 1)}
              disabled={index === 0}
              className="absolute top-1/2 left-2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background/80 text-foreground shadow disabled:opacity-40"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Next submission"
              onClick={() => goTo(index + 1)}
              disabled={index === submissions.length - 1}
              className="absolute top-1/2 right-2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background/80 text-foreground shadow disabled:opacity-40"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-5 p-6">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {index + 1} of {submissions.length}
              </span>
              <span>{formatRelativeTime(current.submittedAt)}</span>
            </div>

            {/* The task being judged — clue, description, and reference image
                so the facilitator verifies against what the players saw. */}
            {(current.challenge.clue || current.challenge.description || current.challenge.imageUrl) && (
              <div className="space-y-2 rounded-lg border bg-muted/30 p-4">
                <span className={MICRO_LABEL}>Challenge — {current.challenge.name}</span>
                <div className="flex items-start gap-3">
                  {current.challenge.imageUrl && (
                    <img
                      src={current.challenge.imageUrl}
                      alt={`${current.challenge.name} reference`}
                      className="h-16 w-16 shrink-0 rounded-md object-cover"
                    />
                  )}
                  <div className="min-w-0 flex-1 space-y-1">
                    {current.challenge.clue && (
                      <RichTextDisplay content={current.challenge.clue} className="text-sm" />
                    )}
                    {current.challenge.description && (
                      <p className="text-sm text-muted-foreground">
                        {current.challenge.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {confidencePct != null && (
              <div className="space-y-2 rounded-lg border bg-muted/30 p-4">
                <div className="flex items-center justify-between">
                  <span className={cn(MICRO_LABEL, 'flex items-center gap-1')}>
                    <Sparkles className="h-3 w-3" aria-hidden="true" /> AI verdict
                  </span>
                  <span className="font-mono text-sm font-medium text-foreground">
                    {confidencePct}%
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${confidencePct}%` }}
                    role="progressbar"
                    aria-valuenow={confidencePct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
                {current.aiReasoning && (
                  <p className="text-sm text-muted-foreground">{current.aiReasoning}</p>
                )}
              </div>
            )}

            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className={MICRO_LABEL}>Submitted by</dt>
                <dd className="mt-1 text-foreground">{current.displayName}</dd>
              </div>
              <div>
                <dt className={MICRO_LABEL}>Challenge points</dt>
                <dd className="mt-1 text-foreground">{current.challenge.points}</dd>
              </div>
            </dl>

            <div className="space-y-1.5">
              <Label htmlFor="review-points">Points to award</Label>
              <Input
                id="review-points"
                type="number"
                min={0}
                value={points}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPoints(Number(e.target.value))
                }
                disabled={!isPending}
              />
            </div>
          </div>
        </div>

        {rejecting && isPending ? (
          <div className="space-y-3 border-t px-6 py-4">
            <Label>Why is this being rejected?</Label>
            <div className="flex flex-wrap gap-1.5">
              {REJECTION_REASONS.map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setReasonChoice((prev) => (prev === code ? null : code))}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs transition-colors',
                    reasonChoice === code && !customReason.trim()
                      ? 'border-destructive bg-destructive/10 text-destructive'
                      : 'border-border text-muted-foreground hover:bg-muted',
                  )}
                >
                  {code}
                </button>
              ))}
            </div>
            <Input
              value={customReason}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomReason(e.target.value)}
              placeholder="Or write your own reason…"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              The team sees this reason with a prompt to resubmit.
            </p>
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setRejecting(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => submitReview(REVIEW_ACTION.reject)}
                disabled={!rejectionReason || review.isPending}
              >
                <X className="mr-1.5 h-4 w-4" /> Confirm reject
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col-reverse gap-3 border-t px-6 py-4 sm:flex-row sm:justify-end">
            <Button
              variant="destructive"
              onClick={() => setRejecting(true)}
              disabled={!isPending || review.isPending}
            >
              <X className="mr-1.5 h-4 w-4" /> Reject (R)
            </Button>
            <Button
              onClick={() => submitReview(REVIEW_ACTION.approve)}
              disabled={!isPending || review.isPending}
            >
              <Check className="mr-1.5 h-4 w-4" /> Approve (A)
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
